/**
 * Map Inspector (_docs/tilemap/05 §8). Map-level fields (name, cell_size — with
 * a tooltip that it's export metadata) and stats, plus the active layer's
 * collider/physics properties (engine 47.3's set: generate_colliders + friction/
 * restitution/density) which travel to the engine on export.
 */

import { useState } from 'react';
import { useTilemapStore, tilemapStore } from '../store/tilemapStore';
import { tilemapClient } from '../api/tilemapClient';

export function MapInspector({ projectUuid }: { projectUuid: string }) {
    const doc = useTilemapStore((s) => s.doc);
    const activeLayerUuid = useTilemapStore((s) => s.activeLayerUuid);
    if (!doc) return null;

    const layer = doc.layers.find((l) => l.uuid === activeLayerUuid) ?? null;
    const totalTiles = doc.layers.reduce((n, l) => n + l.chunk_index.reduce((m, c) => m + c.used_count, 0), 0);

    const patchMap = async (data: Record<string, unknown>) => {
        await tilemapClient.updateTilemap(projectUuid, doc.tilemap.uuid, data);
        await tilemapStore.getState().reload();
    };
    const patchLayer = async (data: Record<string, unknown>) => {
        if (!layer) return;
        await tilemapClient.updateLayer(layer.uuid, data);
        await tilemapStore.getState().reload();
    };

    return (
        <div className="p-2 small">
            <div className="fw-bold mb-2">Map</div>
            <label className="form-label mb-0">Name</label>
            <input className="form-control form-control-sm mb-2" defaultValue={doc.tilemap.name}
                onBlur={(e) => { if (e.target.value !== doc.tilemap.name) void patchMap({ name: e.target.value }); }} />

            <label className="form-label mb-0" title="World units per tile — export metadata consumed by the engine; invisible on the web canvas.">
                Cell size <i className="ti ti-info-circle text-secondary" />
            </label>
            <input type="number" step={0.1} min={0.0001} className="form-control form-control-sm mb-2" defaultValue={doc.tilemap.cell_size}
                onBlur={(e) => { const v = Number(e.target.value); if (v && v !== doc.tilemap.cell_size) void patchMap({ cell_size: v }); }} />

            <div className="text-secondary mb-3">
                {doc.layers.length} layers · {totalTiles} tiles painted
            </div>

            {layer && (
                <>
                    <div className="fw-bold mb-2">Layer: {layer.name}</div>
                    <label className="form-check mb-2">
                        <input type="checkbox" className="form-check-input" checked={layer.generate_colliders}
                            onChange={(e) => void patchLayer({ generate_colliders: e.target.checked })} />
                        <span className="form-check-label" title="Solid tiles become greedy-meshed colliders on export to the engine (47.1).">
                            Generate colliders
                        </span>
                    </label>
                    {layer.generate_colliders && (
                        <div className="row g-2">
                            <PhysField label="Friction" value={layer.physics?.friction ?? 0.5} onSave={(v) => patchLayer({ physics: { ...layer.physics, friction: v } })} />
                            <PhysField label="Restitution" value={layer.physics?.restitution ?? 0} onSave={(v) => patchLayer({ physics: { ...layer.physics, restitution: v } })} />
                            <PhysField label="Density" value={layer.physics?.density ?? 1} onSave={(v) => patchLayer({ physics: { ...layer.physics, density: v } })} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function PhysField({ label, value, onSave }: { label: string; value: number; onSave: (v: number) => void }) {
    const [v, setV] = useState(value);
    return (
        <div className="col-4">
            <label className="form-label mb-0">{label}</label>
            <input type="number" step={0.1} className="form-control form-control-sm" value={v}
                onChange={(e) => setV(Number(e.target.value))} onBlur={() => onSave(v)} />
        </div>
    );
}
